/**
 * Bausteine, die in mehreren Entitaeten vorkommen.
 *
 * Alles hier ist bewusst eng gefasst: Was der Vertrag nicht erlaubt, landet
 * nicht in der Datenbank. Eine Inventarnummer, die im Einsatz falsch getippt
 * wurde, faellt lieber sofort auf als Wochen spaeter in einer Auswertung.
 */
import { z } from "zod";
/** Kennung eines Datensatzes. Buchungen bringen ihre eigene mit (UUID v7). */
export const Id = z.uuid();
/**
 * Zeitpunkt als ISO-8601-Zeichenkette **mit Zeitzonenangabe**.
 *
 * Der Versatz ist Pflicht, weil die Konfliktaufloesung Zeitstempel von
 * Geraeten in unterschiedlichen Zeitzonen vergleicht (KONZEPT.md §15.3). Eine
 * Angabe ohne Versatz waere mehrdeutig und damit unbrauchbar.
 */
export const Zeitpunkt = z.iso.datetime({ offset: true });
/**
 * THW-Inventarnummer: vier Stellen, Bindestrich, sechs Stellen -- dazwischen
 * steht bei manchen Positionen ein Buchstabe, etwa `0148-S001771` bei
 * Sanitaetstaschen.
 *
 * Uebernommen aus dem vorhandenen Inventur-Werkzeug, wo das Muster an echten
 * Ausstattungslisten des Ortsverbands geprueft ist.
 */
export const INVENTARNUMMER_MUSTER = /^\d{4}-[A-Z]?\d{6}$/;
/**
 * Eine echte THW-Inventarnummer -- streng nach dem Muster. So wird geprueft,
 * was ein Mensch eintippt oder eine Liste liefert.
 */
export const ThwInventarnummer = z
    .string()
    .regex(INVENTARNUMMER_MUSTER, "Keine gueltige THW-Inventarnummer (Muster 0141-900639)");
/** Muster einer Lagerortkennung, siehe `Kennung`. */
export const KENNUNG_MUSTER = /^[A-Z0-9-]{1,16}$/;
/**
 * Die Nummer eines Artikels: die THW-Inventarnummer -- oder, bei einem
 * Packstueck ohne eine solche, seine Kennung (ADR 0013 §7, ADR 0016). Etwa
 * neun von zehn Kisten im Ortsverband haben keine THW-Nummer, und jedes
 * Packstueck ist ein Artikel.
 *
 * Beim Lesen gilt deshalb beides. Vergeben wird die Kennung als Nummer nur vom
 * Core, beim Anlegen eines Packstuecks; wer einen Artikel von Hand anlegt,
 * braucht weiter eine THW-Nummer (`ArtikelAnlegen`). Seit Vertrag 2.7 -- eine
 * Erweiterung, weil jede bisherige Nummer gueltig bleibt.
 */
export const Inventarnummer = z.string().regex(
// Die THW-Nummer passt auch ins Kennungsformat. Beide stehen trotzdem da,
// damit man liest, was gemeint ist.
/^(\d{4}-[A-Z]?\d{6}|[A-Z0-9-]{1,16})$/, "Keine gueltige Nummer: THW-Inventarnummer (0141-900639) oder Kennung eines Packstuecks");
/**
 * Kurze Kennung eines Lagerorts, z. B. `GB-07` oder `LKW-1`.
 *
 * Grossbuchstaben, Ziffern und Bindestrich. Die Kennung steht auf dem Etikett
 * und wird unter schlechten Bedingungen abgelesen und vorgelesen -- Klein- und
 * Sonderzeichen machen das unnoetig schwer.
 */
export const Kennung = z
    .string()
    .min(1)
    .max(16)
    .regex(KENNUNG_MUSTER, "Kennung darf nur Grossbuchstaben, Ziffern und Bindestrich enthalten");
/**
 * Zeichen eines Kurzcodes: Ziffern und Grossbuchstaben ohne I, L, O und U
 * (Crockford). Nichts, was man mit einer 1 oder einer 0 verwechselt.
 */
export const KURZCODE_ZEICHEN = "0123456789ABCDEFGHJKMNPQRSTVWXYZ";
/** Muster eines Kurzcodes, siehe `Kurzcode`. */
export const KURZCODE_MUSTER = /^[0-9A-HJKMNP-TV-Z]{5}$/;
/**
 * Der feste Kurzcode eines Lagerorts im QR-Code, etwa `K7M2Q` (ADR 0013 §12,
 * ADR 0017).
 *
 * Fuenf Zeichen, zufaellig, von der Datenbank beim Anlegen vergeben. Er
 * aendert sich nie und wird nie neu vergeben -- anders als die Kennung, die
 * nach dem Pilotbetrieb umbenannt werden darf. So zeigt ein gedrucktes
 * Etikett auch nach dem Umbenennen noch auf seinen Ort. Seit Vertrag 2.8.
 */
export const Kurzcode = z.string().regex(KURZCODE_MUSTER, "Kein gueltiger Kurzcode");
/** Freitext, der in der Oberflaeche in eine Zeile passen muss. */
export const KurzText = z.string().trim().min(1).max(200);
/** Freitext ohne enge Grenze, etwa Beschreibungen und Kommentare. */
export const LangText = z.string().trim().max(4000);
/**
 * Kennung des erfassenden Geraets. Sie dient zwei Zwecken: Sie steht im
 * Protokoll, und sie entscheidet Gleichstaende bei der Konfliktaufloesung
 * (siehe `zustandBerechnen`). Deshalb ist sie Pflicht und nicht leer.
 */
export const GeraeteKennung = z.string().trim().min(1).max(64);
//# sourceMappingURL=basis.js.map